<script setup lang="ts">
import { RouterView } from 'vue-router'
import Navbar from '@/components/common/navbar/Navbar.vue'
import Footer from '@/components/common/footer/Footer.vue'
import AdminNav from '@/components/admin/admin-nav/AdminNav.vue'
import GameNavbar from '@/components/common/Global/game-navbar/GameNavbar.vue'
import CookieBanner from '@/components/common/Global/cookies/CookieBanner.vue'
import CookieButton from '@/components/common/Global/cookies/CookieButton.vue'
import PWAUpdatePrompt from '@/components/common/PWAUpdatePrompt.vue'
import { applyCookies, getAcceptedCookies, applyCookiesToGtag } from '@/stores/cookies'
import type { AcceptedCookies } from '@/types/Types'
import { ref, onMounted } from 'vue'
import { updateSW } from './main'

const cookiesNotAknowledged = ref(false)
const displayCookies = ref(false)
const updatePromptRef = ref<InstanceType<typeof PWAUpdatePrompt> | null>(null)

const applyCookiePreferences = (acceptedCookies: AcceptedCookies) => {
  applyCookies(acceptedCookies)
  applyCookiesToGtag(acceptedCookies)
  cookiesNotAknowledged.value = false
  displayCookies.value = false
}

onMounted(() => {
  cookiesNotAknowledged.value = !getAcceptedCookies()

  // Listen for PWA update events
  window.addEventListener('pwa-update-available', () => {
    if (updatePromptRef.value) {
      updatePromptRef.value.showPrompt()
    }
  })
})
</script>

<template>
  <Suspense>
    <main>
      <Navbar v-if="!$route.path.includes('/admin') && !$route.path.includes('/game')" />
      <!-- GameNavbar without props for /game/* routes that are not the active
           game session (main-menu, pregame, disclaimer-game). Game.vue renders
           its own GameNavbar with onAutoRoll and cap wired (DEC-046 option a). -->
      <GameNavbar v-if="$route.path.includes('/game') && $route.name !== 'game'" />
      <div class="container">
        <AdminNav v-if="$route.path.includes('/admin')" />
        <Transition name="main-transition">
          <RouterView v-slot="{ Component }" :key="$route.fullPath">
            <component :is="Component"></component>
          </RouterView>
        </Transition>
        <Footer v-if="!$route.path.includes('/admin') && !$route.path.includes('/game')" />
        <CookieButton v-if="!$route.path.includes('/game')" @click="displayCookies = true" />
        <CookieBanner
          @close="applyCookiePreferences"
          v-if="cookiesNotAknowledged || displayCookies"
        />
        <PWAUpdatePrompt ref="updatePromptRef" :updateSW="updateSW" />
      </div>
    </main>
  </Suspense>
</template>

<style lang="scss">
@import './style/global.style.scss';
@import url('https://cdn-uicons.flaticon.com/uicons-solid-rounded/css/uicons-solid-rounded.css');
@import url('https://cdn-uicons.flaticon.com/uicons-bold-rounded/css/uicons-bold-rounded.css');
@import url('https://cdn-uicons.flaticon.com/uicons-solid-straight/css/uicons-solid-straight.css');
@import url('https://cdn-uicons.flaticon.com/uicons-thin-straight/css/uicons-thin-straight.css');
@import url('https://cdn-uicons.flaticon.com/uicons-regular-rounded/css/uicons-regular-rounded.css');
@import url('https://cdn-uicons.flaticon.com/uicons-brands/css/uicons-brands.css');

main {
  background-image: url('/shapes/logo-yellow-grid.svg');
  background-repeat: repeat;
  background-position: center top;

  @include breakpoint-md {
    div {
      display: block;
    }
  }
}

.main-transition-enter-active,
.main-transition-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.main-transition-enter-from,
.main-transition-leave-to {
  opacity: 0;
}
</style>
